/**
 * Tooltip Component
 *
 * A lightweight tooltip component that displays additional information
 * when hovering over an element.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/tailwind-utils'

interface TooltipProps {
  /**
   * The element that triggers the tooltip
   */
  children: React.ReactNode

  /**
   * The content to display in the tooltip
   */
  content: React.ReactNode

  /**
   * The position of the tooltip relative to the trigger
   * @default "top"
   */
  position?: 'top' | 'right' | 'bottom' | 'left'

  /**
   * The delay before showing the tooltip (in ms)
   * @default 300
   */
  delay?: number

  /**
   * Additional CSS classes for the tooltip
   */
  className?: string

  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean

  /**
   * The maximum width of the tooltip
   * @default "200px"
   */
  maxWidth?: string
}

/**
 * Tooltip component for displaying additional information on hover
 *
 * @example
 * // Basic usage
 * <Tooltip content="Additional information">
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * @example
 * // With custom position and delay
 * <Tooltip
 *   content="This appears on the right after 500ms"
 *   position="right"
 *   delay={500}
 * >
 *   <InfoIcon />
 * </Tooltip>
 */
function Tooltip({
  children,
  content,
  position = 'top',
  delay = 300,
  className,
  disabled = false,
  maxWidth = '200px',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  // Handle mounting state for SSR compatibility
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  // Calculate tooltip position
  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()

    let x = 0
    let y = 0

    switch (position) {
      case 'top':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        y = triggerRect.top - tooltipRect.height - 8
        break
      case 'right':
        x = triggerRect.right + 8
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        break
      case 'bottom':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        y = triggerRect.bottom + 8
        break
      case 'left':
        x = triggerRect.left - tooltipRect.width - 8
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        break
    }

    // Ensure tooltip stays within viewport
    const padding = 10
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Adjust horizontal position
    if (x < padding) {
      x = padding
    } else if (x + tooltipRect.width > viewportWidth - padding) {
      x = viewportWidth - tooltipRect.width - padding
    }

    // Adjust vertical position
    if (y < padding) {
      y = padding
    } else if (y + tooltipRect.height > viewportHeight - padding) {
      y = viewportHeight - tooltipRect.height - padding
    }

    setCoords({ x, y })
  }

  // Show tooltip after delay
  const handleMouseEnter = () => {
    if (disabled) return

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
      // Wait for next frame to calculate position after tooltip is rendered
      requestAnimationFrame(() => calculatePosition())
    }, delay)
  }

  // Hide tooltip and clear timeout
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsVisible(false)
  }

  // Update position on window resize
  useEffect(() => {
    calculatePosition()
    window.addEventListener('resize', calculatePosition)
    window.addEventListener('scroll', calculatePosition)

    return () => {
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition)
    }
  }, [isVisible, calculatePosition])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Position classes for arrow
  const arrowPositionClasses = {
    top: 'bottom-[-4px] left-1/2 transform -translate-x-1/2 rotate-45',
    right: 'left-[-4px] top-1/2 transform -translate-y-1/2 rotate-45',
    bottom: 'top-[-4px] left-1/2 transform -translate-x-1/2 rotate-45',
    left: 'right-[-4px] top-1/2 transform -translate-y-1/2 rotate-45',
  }

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}

      {isMounted &&
        isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'fixed z-50 px-3 py-1.5 text-sm bg-card text-card-foreground border border-border rounded shadow-md animate-in fade-in-0 zoom-in-95 duration-200',
              className
            )}
            style={{
              left: `${coords.x}px`,
              top: `${coords.y}px`,
              maxWidth,
            }}
            role="tooltip"
          >
            {content}
            <div
              className={cn(
                'absolute w-2 h-2 bg-card border-border',
                arrowPositionClasses[position]
              )}
              style={{
                borderWidth:
                  position === 'top' || position === 'left' ? '0 1px 1px 0' : '1px 0 0 1px',
              }}
            />
          </div>,
          document.body
        )}
    </div>
  )
}

export default Tooltip
